/**
 * EPIC-041 follow-up — public posts that attach this market via
 * $TICKER. Surfaces inline analysis on the market detail page.
 *
 * Auto-trade posts are excluded server-side (every fill emits one;
 * keep this surface focused on authored analysis). Auto-hides when
 * there are zero matching posts.
 */
"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type PostRecord } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

interface Props {
  marketId: string;
}

export function MarketAttachedPostsSection({ marketId }: Props) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<PostRecord[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .listPostsAttachedToMarket(marketId, { limit: 5 })
      .then((res) => {
        if (cancelled) return;
        setPosts(res.posts);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [marketId]);

  if (!loaded || posts.length === 0) return null;

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
        {t("marketAttachedPosts.heading")}
      </h2>
      <ul className="space-y-2">
        {posts.map((p) => (
          <li
            key={p.id}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
          >
            <header className="mb-2 flex items-start gap-2">
              <Avatar
                url={p.authorAvatarUrl}
                fallbackChar={p.authorUsername}
                size={28}
                href={`/u/${p.authorUsername}`}
                alt={p.authorDisplayName ?? p.authorUsername}
              />
              <div className="min-w-0 flex-1">
                <Link
                  href={`/u/${p.authorUsername}`}
                  className="block truncate text-[12px] font-bold text-[var(--color-text)] hover:text-[var(--color-brand)]"
                >
                  {p.authorDisplayName ?? `@${p.authorUsername}`}
                </Link>
                <div className="text-[10px] text-[var(--color-text3)]">
                  @{p.authorUsername} ·{" "}
                  {new Date(p.createdAt).toLocaleDateString()}
                </div>
              </div>
            </header>
            <Link
              href={`/posts/${p.id}`}
              className="block transition-colors hover:opacity-80"
            >
              {p.title && (
                <div className="mb-1 line-clamp-1 text-[13px] font-bold text-[var(--color-text)]">
                  {p.title}
                </div>
              )}
              <p className="line-clamp-3 text-[12px] text-[var(--color-text2)]">
                {p.bodyMd}
              </p>
              <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--color-text3)]">
                {p.likeCount > 0 && (
                  <span>
                    {p.likeCount} {p.likeCount === 1 ? "like" : "likes"}
                  </span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
